import React from 'react'
import PropTypes from 'prop-types'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { joinOrEmpty, workerFullName } from '../../../utils/jobDetailFormat'
import {
  emptyWhenKey,
  footprintVerdict,
  formatDuration,
  formatWhen,
  inspectionsSummary,
  machineEmptyKey,
  materialsSummary,
  resultLabelKey,
  sortIncidences,
  statusLineKind,
  verdictIncidencesSummary,
  workerEmptyKey
} from '../../../utils/jobDetailFootprint'
import { Pill } from '../JobDetailPrimitives'
import ProvenanceMark from './ProvenanceMark'

// Historia 169, enmienda UX — GATE 2 bis, UX-1: la frase de estado sustituye la rejilla 3×2. Segmentos
// separados por " · ", solo los que existen (RN4-RN9, RN14, H3, H17). `footprint-result`,
// `footprint-when`, `footprint-duration`, `footprint-machine` y `footprint-worker` se conservan como
// testid de SEGMENTO (S73); `footprint-incidences` es el grupo de chips de la segunda línea. UX-7: la
// marca junto al dato deducido, nunca en un rótulo.
//
// UX-11 (2026-09-27, decisión del PO, FIRMADO — S97, `features/S92-enmienda-ux-jupiter.feature`):
// sustituye la cabecera "n intentos, m cierres manuales" (UX-2) y el chip "Manual" de la segunda línea
// por una línea de VEREDICTO arriba de la frase de estado (`footprint-verdict`, `data-tone`) — ambos
// contaban filas sembradas por la siembra de #116 (`manual = true` en toda entrada al job, RN3 sin
// cambio), no un cierre manual real. El veredicto se calcula SOLO con D6 base
// (`footprintVerdict`, jobDetailFootprint.js), sin llamada nueva.

const RESULT_TONE = { OK: 'success', FAIL: 'error', PAUSED: 'warning', NOTOOL: 'warning', CANCELLED: 'default' }
const VERDICT_ICON = { success: CheckCircleOutlineIcon, warning: WarningAmberOutlinedIcon, error: ErrorOutlineIcon, neutral: RemoveCircleOutlineIcon }
const VERDICT_COLOR = { success: 'success.main', warning: 'yellow.main', error: 'error.light', neutral: 'text.secondary' }

const FootprintStatusLine = ({ data = {}, formatter }) => {
  const { t_ } = useSmartTranslation()
  const execution = data.execution || {}
  const device = data.device || null
  const workers = data.workers || []
  const partState = data.state || ''
  const kind = statusLineKind(execution, partState)
  const incidences = sortIncidences(data.affectingIncidences || [])

  // S62: sin resultado registrado (CLOSED, `result` null) es TEXTO, no chip — solo hay chip cuando hay
  // un resultado real, o siempre para la marca de cancelación/CANCELLED_EMPTY (forzado a CANCELLED).
  const showResultChip = (kind === 'CLOSED' && !!execution.result) || kind === 'CANCELLATION_MARK' || kind === 'CANCELLED_EMPTY'
  const forcedResult = kind === 'CANCELLED_EMPTY' ? 'CANCELLED' : execution.result
  const resultLabel = t_(resultLabelKey(forcedResult))
  const resultTone = RESULT_TONE[forcedResult] || 'default'

  const machineEmpty = machineEmptyKey(execution, device)
  const workerEmpty = workerEmptyKey(execution, workers)

  // UX-11: el veredicto se calcula SOLO con D6 base — nada de esto es una llamada nueva. Sin ejecución
  // (tono neutro) los tres resúmenes quedan fuera: "Sin incidencias"/"Sin materiales…" no aportan nada
  // junto a un rótulo que ya dice que no hay nada que reportar (evita el "vacío sobre vacío", S64/S87).
  const verdict = footprintVerdict(execution, data.materials || {}, data.inspections || {}, data.affectingIncidences || [], partState)
  const VerdictIcon = VERDICT_ICON[verdict.tone] || RemoveCircleOutlineIcon
  const joinSegments = (segments) => segments.map((segment) => t_(segment.key, segment.params)).join(' · ')
  const verdictText = verdict.tone === 'neutral'
    ? t_(verdict.labelKey)
    : [
        t_(verdict.labelKey),
        joinSegments(materialsSummary(data.materials || {})),
        joinSegments(inspectionsSummary(data.inspections || {})),
        joinSegments(verdictIncidencesSummary(data.affectingIncidences || []))
      ].filter(Boolean).join(' · ')

  const MachineSegment = () => (
    <Typography component='span' data-testid='footprint-machine'>
      {device ? device.name : t_(machineEmpty)}
      {device ? <ProvenanceMark provenance={data.deviceProvenance} testId='footprint-machine-provenance' /> : null}
    </Typography>
  )

  const WorkerSegment = () => (
    <Typography component='span' data-testid='footprint-worker'>
      {workers.length > 0 ? joinOrEmpty(workers.map(workerFullName)) : t_(workerEmpty)}
      {workers.length > 0 ? <ProvenanceMark provenance={data.workersProvenance} testId='footprint-worker-provenance' /> : null}
    </Typography>
  )

  // B-J-space (defecto real de navegador, mic1): un navegador real recorta el espacio en el BORDE de
  // cada flex-item cuando los segmentos son hijos DIRECTOS del `Stack` en fila, separados por texto
  // suelto " · " — cada segmento se convierte en su propia "línea" a efectos de colapso de blancos
  // (jsdom no ejecuta layout CSS, por eso no lo reproduce). El arreglo: cuándo/duración/máquina/
  // operario viven TODOS dentro de un único envoltorio no-flex (ver el `<Typography>` que los agrupa en
  // el `return` de más abajo) — dentro de él el espacio " · " es texto interior normal, nunca el borde
  // de un flex-item, así que un navegador real no lo recorta.
  const renderWhenAndDuration = () => {
    if (kind === 'CLOSED') {
      const whenText = formatWhen(execution.dateStart, execution.dateEnd, formatter)
      let durationText = ''
      if (execution.durationMillis === null || execution.durationMillis === undefined) {
        durationText = t_('Duration cannot be calculated')
      } else {
        durationText = formatDuration(execution.durationMillis, t_)
      }
      return (
        <>
          <Typography component='span' data-testid='footprint-when'>{whenText}</Typography>
          {' · '}<Typography component='span' data-testid='footprint-duration'>{durationText}</Typography>
          {' · '}<MachineSegment />
          {' · '}<WorkerSegment />
        </>
      )
    }
    if (kind === 'CANCELLATION_MARK') {
      const instant = execution.dateEnd || execution.dateStart
      return (
        <>
          <Typography component='span' data-testid='footprint-when'>{formatWhen(instant, null, formatter)}</Typography>
          {' · '}<Typography component='span' data-testid='footprint-duration'>{t_('Cancellation mark')}</Typography>
          {' · '}<MachineSegment />
          {' · '}<WorkerSegment />
        </>
      )
    }
    if (kind === 'OPEN') {
      // UX-1: "En marcha desde {{date}}" se funde con "en {{machine}}" SIN " · " cuando hay máquina (una
      // sola frase); sin máquina, el vacío de RN14 va COMO SEGMENTO propio, con " · ".
      return (
        <>
          <Typography component='span' data-testid='footprint-when'>
            {t_('In progress since {{date}}', { date: formatWhen(execution.dateStart, null, formatter) })}
          </Typography>
          {device
            ? (
              <Typography component='span' data-testid='footprint-machine'>
                {' ' + t_('on {{machine}}', { machine: device.name })}
                <ProvenanceMark provenance={data.deviceProvenance} testId='footprint-machine-provenance' />
              </Typography>
              )
            : (<>{' · '}<Typography component='span' data-testid='footprint-machine'>{t_(machineEmpty)}</Typography></>)}
          {' · '}<WorkerSegment />
        </>
      )
    }
    // CANCELLED_EMPTY / EMPTY: una sola frase de vacío, sin máquina/operario/duración.
    return (
      <Typography component='span' data-testid='footprint-when'>{t_(emptyWhenKey(execution, partState))}</Typography>
    )
  }

  const hasReason = !!execution.resultDescription
  // S64/S69: sin ejecución (EMPTY/CANCELLED_EMPTY) la segunda línea no se pinta NADA, ni siquiera un
  // contenedor vacío de incidencias; en el resto de estados, `footprint-incidences` existe SIEMPRE
  // (aunque no tenga chips dentro), para poder afirmar "no tiene chips" sin que falte el testid (S69).
  const showSecondLine = kind !== 'EMPTY' && kind !== 'CANCELLED_EMPTY'

  // B-J-space: el chip (o el texto plano "Sin resultado registrado") es un flex-item SEPARADO del
  // envoltorio de la frase — el "·" que los separa va en SU PROPIO flex-item (un carácter, sin espacio
  // alrededor en el propio texto: el hueco lo pone `spacing` del `Stack`, nunca un espacio de borde que
  // un navegador real recortaría).
  const hasPrecedingSegment = showResultChip || (kind === 'CLOSED' && !forcedResult)

  return (
    <>
      {/* UX-11: la línea de veredicto vive FUERA de "footprint-status-line" (que sigue siendo SOLO la
          frase de estado, S73: el resto del panel/los tests existentes no cambian) — un hermano previo,
          mismo `px` que la frase (mismo motivo que ya corregía la antigua cabecera de intentos aquí: el
          `spacing` de un Stack en fila desplaza el SEGUNDO hijo aunque salte de línea por `flexWrap`). */}
      <Box data-testid='footprint-verdict' data-tone={verdict.tone} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, pt: 1.5 }}>
        <VerdictIcon fontSize='small' sx={{ color: VERDICT_COLOR[verdict.tone] }} />
        <Typography sx={{ fontWeight: 700, color: VERDICT_COLOR[verdict.tone] }}>{verdictText}</Typography>
      </Box>
      <Box data-testid='footprint-status-line' sx={{ px: 2, py: 1.5 }}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          {showResultChip
            ? <Pill label={resultLabel} tone={resultTone} testId='footprint-result' />
            : (kind === 'CLOSED' && !forcedResult ? <Typography component='span' data-testid='footprint-result'>{resultLabel}</Typography> : null)}
          {hasPrecedingSegment ? <Box component='span'>·</Box> : null}
          <Typography component='span'>
            {renderWhenAndDuration()}
          </Typography>
        </Stack>
        {showSecondLine
          ? (
            <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' sx={{ mt: 0.5 }}>
              {hasReason ? <Typography variant='body2' color='text.secondary'>&ldquo;{execution.resultDescription}&rdquo;</Typography> : null}
              <Box component='span' data-testid='footprint-incidences'>
                {/* B-J12 (2026-09-27, FIRMADO — S69, hub e049d60): la fecha de cierre lleva día, año Y
                    HORA (UX-3, mismo formato que el resto del panel) — `formatDateTime`, nunca
                    `formatDate` (que la dejaba sin hora, "21/09/2026" a secas). */}
                {incidences.map((incidence) => (
                  <Tooltip
                    key={incidence.uuid}
                    disableInteractive
                    title={incidence.title + ' · ' + t_(incidence.status) + (incidence.status === 'CLOSED' ? ' · ' + t_('Closed on {{date}}', { date: formatter.formatDateTime(incidence.closedAt) }) : '')}
                  >
                    <Box component='span' sx={{ display: 'inline-block' }}>
                      <Pill label={incidence.code} tone={incidence.status === 'CLOSED' ? 'default' : 'warning'} testId={'footprint-incidence-' + incidence.code} />
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Stack>
            )
          : null}
      </Box>
    </>
  )
}

FootprintStatusLine.propTypes = { data: PropTypes.object, formatter: PropTypes.object }

export default React.memo(FootprintStatusLine)
